import { useEffect, useState, useCallback } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatApiError, downloadInvoicePdf, downloadPdf, fileUrl } from "@/lib/api";
import OrderMiniMap from "@/components/OrderMiniMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Package, Users, Tag, Truck, FileText, CreditCard, Settings as SettingsIcon,
  Plus, Trash2, Check, AlertTriangle, Download, Pencil, Repeat, Pause, Play, Mail, MessageSquare, QrCode, Scale, History, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const money = (n) => "£" + Number(n || 0).toFixed(2);
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "clients", label: "Clients", icon: Users },
  { key: "pricing", label: "Price list", icon: Tag },
  { key: "drivers", label: "Drivers & jobs", icon: Truck },
  { key: "invoices", label: "Invoices", icon: FileText },
  { key: "subscription", label: "Subscription", icon: CreditCard },
  { key: "account", label: "Settings", icon: SettingsIcon },
];
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700", collected: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-700", ready: "bg-teal-100 text-teal-700",
  out_for_delivery: "bg-indigo-100 text-indigo-700", completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

import Overview from "./tabs/overview";
import Orders from "./tabs/orders";
import Recurring from "./tabs/recurring";
import Clients from "./tabs/clients";
import Pricing from "./tabs/pricing";
import Drivers from "./tabs/drivers";
import Invoices from "./tabs/invoices";
import Subscription from "./tabs/subscription";
import Account from "./tabs/account";

export default function BusinessDashboard() {
  const [tab, setTab] = useState("overview");
  const [me, setMe] = useState(null);

  const loadMe = useCallback(async () => {
    const m = await api.get("/business/me");
    setMe(m.data);
  }, []);
  useEffect(() => { loadMe().catch(() => {}); }, [loadMe]);

  const sub = me?.subscription_state;

  return (
    <DashboardShell title="Business" nav={NAV} active={tab} onSelect={setTab}>
      {sub && !sub.active && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="subscription-banner">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
            <AlertTriangle className="h-4 w-4" /> {sub.reason}
          </div>
          <Button onClick={() => setTab("subscription")} className="rounded-full bg-amber-600 hover:bg-amber-700" data-testid="banner-subscribe-btn">Choose a plan</Button>
        </div>
      )}
      {sub && sub.status === "trialing" && sub.active && (
        <div className="mb-6 rounded-xl border border-teal-200 bg-teal-50 p-3 text-sm text-teal-800" data-testid="trial-banner">
          Free trial · <b>{sub.trial_days_left} days left</b>. <button className="underline" onClick={() => setTab("subscription")}>Subscribe now</button> to keep going.
        </div>
      )}

      {tab === "overview" && <Overview />}
      {tab === "orders" && <Orders reload={loadMe} />}
      {tab === "recurring" && <Recurring />}
      {tab === "clients" && <Clients reload={loadMe} />}
      {tab === "pricing" && <Pricing />}
      {tab === "drivers" && <Drivers />}
      {tab === "invoices" && <Invoices />}
      {tab === "subscription" && <Subscription me={me} reload={loadMe} />}
      {tab === "account" && <Account me={me} reload={loadMe} />}
    </DashboardShell>
  );
}
